import React from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/Header';
import CampaignList from './CampaignList';
import CampaignEditor from './CampaignEditor';
import HistoryTab from './HistoryTab';
import EvaluationTab from './EvaluationTab';
import ClientConfigTab from './ClientConfigTab';
import FollowupTab from './FollowupTab';
import CopyScoreTab from './CopyScoreTab';

const TAB_NAMES = [
  'campaigns',
  'editor',
  'history',
  'evaluation',
  'config',
  'followup',
  'copy-score',
] as const;
type TabName = (typeof TAB_NAMES)[number];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`webinar-mailings-tabpanel-${index}`}
      aria-labelledby={`webinar-mailings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const WebinarMailings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive main tab state from URL
  const tabParam = searchParams.get('tab') || 'campaigns';
  const tabName = TAB_NAMES.includes(tabParam as TabName) ? (tabParam as TabName) : 'campaigns';
  const activeTab = TAB_NAMES.indexOf(tabName);
  const editingCampaignId =
    tabName === 'editor'
      ? searchParams.get('id')
        ? parseInt(searchParams.get('id')!, 10)
        : null
      : null;

  // Followup state from URL
  const followupSubtab = searchParams.get('sub') || 'campaigns';
  const followupEditId = searchParams.get('edit');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTabName = TAB_NAMES[newValue];
    const params = new URLSearchParams();
    params.set('tab', newTabName);
    setSearchParams(params);
  };

  const handleEditCampaign = (campaignId: number) => {
    setSearchParams({ tab: 'editor', id: String(campaignId) });
  };

  const handleNewCampaign = () => {
    setSearchParams({ tab: 'editor' });
  };

  const handleCampaignSaved = () => {
    setSearchParams({ tab: 'campaigns' });
  };

  const handleFollowupSubtabChange = (subtab: string) => {
    setSearchParams({ tab: 'followup', sub: subtab });
  };

  const handleFollowupEditChange = (editId: string | null) => {
    if (editId) {
      setSearchParams({ tab: 'followup', edit: editId });
    } else {
      setSearchParams({ tab: 'followup' });
    }
  };

  return (
    <Box m="20px">
      <Header title="Webinar Mailings" subtitle="Heat-Up und Redlink Mailings verwalten" />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="webinar mailings tabs">
          <Tab label="Kampagnen" />
          <Tab label={editingCampaignId ? 'Kampagne bearbeiten' : 'Neue Kampagne'} />
          <Tab label="Verlauf" />
          <Tab label="Auswertung" />
          <Tab label="Kunden-Konfiguration" />
          <Tab label="Nachfass" />
          <Tab label="Copy-Score" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <CampaignList onEdit={handleEditCampaign} onNew={handleNewCampaign} />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <CampaignEditor
          campaignId={editingCampaignId}
          onSaved={handleCampaignSaved}
          onCancel={() => setSearchParams({ tab: 'campaigns' })}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <HistoryTab />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <EvaluationTab />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <ClientConfigTab />
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <FollowupTab
          subtab={followupSubtab}
          editId={followupEditId}
          onSubtabChange={handleFollowupSubtabChange}
          onEditChange={handleFollowupEditChange}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <CopyScoreTab />
      </TabPanel>
    </Box>
  );
};

export default WebinarMailings;
